// Stored values the grid would never write: a change plan or a direct `/api/crud` call saves each
// record as given, and the CRUD API checks only its field kinds (P9). The pages and the reports keep
// showing such a line, with what is wrong; editing the field, or Approve for a baseline, fixes it.
// One place for the checks, so the grid's marker and the reports' flag cannot disagree.

import { isDay, shiftDayMonths } from "@axny/platform-contracts";
import { ROUTING_LINES } from "../entities";
import type { BaselineField } from "./baseline";
import { BASELINE_FIELDS, BASELINE_ID_PREFIX, baselineOf, decodeBaseline } from "./baseline";
import { decodeConflicts } from "./carry";
import { isSoNo } from "./deposco";
import { FIELD_LIST, isMulti, splitValues } from "./lists";
import { PE_CLEARED } from "./pe-order";
import { routingStatusValues } from "./routing-status";
import type { LinePickTicketRecord, ListValueRecord, RoutingLineRecord, WarehouseField } from "./types";

/** One value a rule would refuse: the field's header and why. */
export interface OutsideChange {
  field: WarehouseField | BaselineField | "pickTicket" | "carryConflicts";
  label: string;
  why: string;
}

const label = (name: string) => ROUTING_LINES.fields.find((f) => f.name === name)?.label ?? name;

/** "READY DATE — not a date; Lead — not in the Lead list" */
export const outsideText = (changes: readonly OutsideChange[]) =>
  changes.map((c) => `${c.label} — ${c.why}`).join("; ");

const DATE_FIELDS = ["readyDate", "schedShip", "routedOn", "appointmentDate"] as const;
/** Dates only, never ASAP. */
const STRICT_DATES: readonly string[] = ["routedOn", "appointmentDate"];

/** `checkDate`'s range rule, as a stored value is read later: before 2025, or over a year ahead. */
function dateProblem(field: (typeof DATE_FIELDS)[number], v: string, today: string | undefined): string | null {
  if (!v || (!STRICT_DATES.includes(field) && v === "ASAP")) return null;
  if (!isDay(v)) return STRICT_DATES.includes(field) ? "not a date" : "not a date or ASAP";
  if (v < "2025-01-01") return "a date before 2025";
  if (today && v > shiftDayMonths(today, 12)) return "a date more than a year ahead";
  return null;
}

/** The warehouse's fields of a stored line that a rule would refuse. Without `listValues`, lists are not checked. */
export function fieldChanges(
  line: RoutingLineRecord,
  listValues: readonly ListValueRecord[] | undefined,
  today: string | undefined,
): OutsideChange[] {
  const out: OutsideChange[] = [];
  for (const field of DATE_FIELDS) {
    const v = line[field] === null || line[field] === undefined ? "" : String(line[field]).slice(0, 40);
    const why = dateProblem(field, v.length > 10 && isDay(v.slice(0, 10)) ? v.slice(0, 10) : v, today);
    if (why) out.push({ field, label: label(field), why });
  }
  if (line.carryConflicts && !Object.keys(decodeConflicts(line.carryConflicts)).length) {
    out.push({
      field: "carryConflicts",
      label: label("carryConflicts"),
      why: "unreadable — choosing a value writes it again",
    });
  }
  if (listValues) {
    for (const [field, list] of Object.entries(FIELD_LIST) as [
      WarehouseField,
      NonNullable<(typeof FIELD_LIST)[WarehouseField]>,
    ][]) {
      // PE Order Status emptied on purpose (its marker) is no value of its list.
      const v = line[field] && line[field] !== PE_CLEARED ? String(line[field]) : "";
      // Work Needed: each of its values.
      const off = (isMulti(field) ? splitValues(v) : v ? [v] : []).filter(
        (one) => !listValues.some((x) => x.list === list && x.value === one),
      );
      if (off.length) out.push({ field, label: label(field), why: `not in the ${list} list` });
    }
    // Routing status: a value of its list (retired ones too), else of the starting seven.
    const status = line.routingStatus ? String(line.routingStatus) : "";
    if (status && !routingStatusValues(listValues).some((s) => s.value === status))
      out.push({ field: "routingStatus", label: label("routingStatus"), why: "not in the Routing status list" });
  }
  return out;
}

const okNumber = (v: unknown) => v === null || v === undefined || (typeof v === "number" && Number.isFinite(v));
const okDay = (v: unknown) =>
  v === null || v === undefined || v === "" || (typeof v === "string" && isDay(v.slice(0, 10)));

/** Why a copy could not be what Approve writes, or null. */
export function copyProblem(c: Partial<LinePickTicketRecord>): string | null {
  const pt = String(c.pickTicket ?? "");
  // A2000 pick tickets are numbers; a Deposco SO is its SO # (SO230792).
  if (!/^\d{1,12}$/.test(pt) && !isSoNo(pt)) return "not a pick ticket or SO number";
  if (!okNumber(c.qty) || !okNumber(c.routingOpenBdays) || !okNumber(c.routingDueBdays))
    return "a number that is not one";
  if (!okDay(c.ptDate) || !okDay(c.startDate) || !okDay(c.cancelDate)) return "a date that is not one";
  return null;
}

/**
 * A line's baseline as copies, or what is wrong with it: an unknown version, text that does not
 * decode, or a copy Approve could not have written. A bad baseline gives no copies — its pick
 * tickets then show as joined (UPDATED), and Approve writes it again from A2000.
 */
export function readBaseline(line: RoutingLineRecord): {
  copies: LinePickTicketRecord[];
  problem: OutsideChange | null;
} {
  const text = baselineOf(line);
  if (!text) return { copies: [], problem: null };
  const bad = (why: string) => ({
    copies: [],
    problem: { field: BASELINE_FIELDS[0], label: "Pick tickets as approved", why: `${why} — Approve writes it again` },
  });
  if (!text.startsWith("v1|"))
    return bad(/^v\d+\|/.test(text) ? `an unknown version (${text.split("|")[0]})` : "unreadable");
  let decoded;
  try {
    decoded = decodeBaseline(text);
  } catch {
    return bad("unreadable");
  }
  if (decoded.some((c) => copyProblem(c) !== null)) return bad("unreadable");
  return {
    copies: decoded.map((c) => ({
      ...c,
      id: `${BASELINE_ID_PREFIX}${line.id}:${c.pickTicket}`,
      lineId: line.id,
      sourceLoadedAt: "",
    })),
    problem: null,
  };
}
